import { Reveal } from "@/components/motion";
import { Button } from "@/components/ui/button";
import { useAppointment } from "@/components/AppointmentContext";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles } from "lucide-react";

export function Eyebrow({ children }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-teal/10 px-4 py-1.5 text-sm font-medium text-teal">
      <Sparkles className="h-3.5 w-3.5" /> {children}
    </span>
  );
}

export function SectionHeading({ eyebrow, title, intro, center, className = "" }) {
  return (
    <div className={`${center ? "mx-auto max-w-3xl text-center" : "max-w-3xl"} ${className}`}>
      {eyebrow && (
        <Reveal><Eyebrow>{eyebrow}</Eyebrow></Reveal>
      )}
      <Reveal delay={0.05}>
        <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.05] tracking-tight text-navy text-balance">
          {title}
        </h2>
      </Reveal>
      {intro && (
        <Reveal delay={0.1}>
          <p className="mt-5 text-lg text-muted-foreground leading-relaxed">{intro}</p>
        </Reveal>
      )}
    </div>
  );
}

// Interior page hero
export function PageHero({ eyebrow, title, intro, image, breadcrumb }) {
  return (
    <section className="relative overflow-hidden bg-navy pt-32 pb-24 lg:pt-40 lg:pb-32">
      <div className="pointer-events-none absolute -top-32 -right-20 h-96 w-96 rounded-full bg-teal/20 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 -left-20 h-80 w-80 rounded-full bg-softblue-500/20 blur-3xl" />
      {image && (
        <div className="pointer-events-none absolute inset-0 opacity-[0.13]">
          <img src={image} alt="" className="h-full w-full object-cover" aria-hidden="true" />
        </div>
      )}
      <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
        {breadcrumb && (
          <Reveal>
            <nav className="mb-6 flex items-center gap-2 text-sm text-white/50" aria-label="Breadcrumb">
              {breadcrumb.map((b, i) => (
                <span key={b.path} className="flex items-center gap-2">
                  {i > 0 && <span className="text-white/25">/</span>}
                  {i < breadcrumb.length - 1 ? (
                    <Link to={b.path} className="hover:text-teal-light transition-colors">{b.name}</Link>
                  ) : (
                    <span className="text-white/80">{b.name}</span>
                  )}
                </span>
              ))}
            </nav>
          </Reveal>
        )}
        {eyebrow && (
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm font-medium text-teal-light">
              {eyebrow}
            </span>
          </Reveal>
        )}
        <Reveal delay={0.05}>
          <h1 className="mt-5 max-w-4xl font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.03] tracking-tight text-white text-balance">
            {title}
          </h1>
        </Reveal>
        {intro && (
          <Reveal delay={0.12}>
            <p className="mt-6 max-w-2xl text-lg lg:text-xl text-white/65 leading-relaxed">{intro}</p>
          </Reveal>
        )}
      </div>
    </section>
  );
}

export function FinalCTA({
  title = "Ready to Feel Better?",
  text = "Whether you're struggling with anxiety, depression, stress, relationship challenges, or simply feeling stuck, we're here to help. Take the first step today.",
}) {
  const { openDialog } = useAppointment();
  return (
    <section className="relative overflow-hidden py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-navy px-8 py-16 sm:px-16 sm:py-24 text-center shadow-premium">
          <div className="pointer-events-none absolute -top-24 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-teal/25 blur-3xl" />
          <div className="pointer-events-none absolute inset-0 grain opacity-[0.15]" />
          <div className="relative">
            <Reveal>
              <h2 className="mx-auto max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white text-balance">
                {title}
              </h2>
            </Reveal>
            <Reveal delay={0.08}>
              <p className="mx-auto mt-6 max-w-2xl text-lg text-white/65 leading-relaxed">{text}</p>
            </Reveal>
            <Reveal delay={0.16}>
              <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <Button onClick={openDialog} data-testid="final-cta-schedule"
                  className="group rounded-full bg-teal hover:bg-teal-dark text-white h-14 px-8 text-base shadow-premium hover:-translate-y-1 transition-transform">
                  Schedule Your Free 15-Minute Consultation
                  <ArrowUpRight className="ml-1 h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Button>
                <Link to="/contact">
                  <Button variant="outline"
                    className="rounded-full border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white h-14 px-8 text-base">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
